import React, { ReactElement, useEffect, useState } from 'react';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { useDispatch } from 'react-redux';
import { Alert, Grid, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import { StripePriceLookupKey, stripePriceLookupKeys } from '@paragon/contract-library';

import {
  cancelSubscription,
  startFreeTrial as startFreeTrialAction,
  uncancelSubscription,
  upgradeToPlan,
} from '../../actions/accountActions';
import { getLoggedInUser, hasSubscriptionManagerPermission } from '../../selectors/users';
import { LoginDataSliceState, setSubscription } from '../../store/logInDataSlice';
import { useAppWithLogInDataSelector } from '../hooks';
import { FREE_PLAN, INDIVIDUAL_DESIGNER_PLAN, INDIVIDUAL_ESTIMATOR_PLAN } from './common';
import { PlanCard } from './PlanCard';

interface UpgradeAccountProps {
  readonly successUrl: string;
  readonly cancelUrl: string;
  readonly message?: string | ReactElement;
  readonly showGreeting?: boolean;
}

export type BillingPeriod = 'month' | 'year';

const monthlyPlans: readonly StripePriceLookupKey[] = ['individual-estimator-monthly', 'individual-designer-monthly'];
const yearlyPlans: readonly StripePriceLookupKey[] = ['individual-estimator-yearly', 'individual-designer-yearly'];

export function UpgradeAccount({ successUrl, cancelUrl, message, showGreeting }: UpgradeAccountProps) {
  const dispatch = useDispatch<ThunkDispatch<LoginDataSliceState, void, Action>>();
  const loggingIn = useAppWithLogInDataSelector((state) => state.logInData.isAutomaticLogInOngoing);
  const loggedInUser = useAppWithLogInDataSelector(getLoggedInUser);
  const isSubscriptionManager = useAppWithLogInDataSelector(hasSubscriptionManagerPermission);
  const subscription = useAppWithLogInDataSelector((state) => state.logInData.subscription);
  const cancelAtPeriodEnd = useAppWithLogInDataSelector((state) => state.logInData.subscriptionCancelsAtPeriodEnd);
  const currentPeriodEnd = useAppWithLogInDataSelector((state) => state.logInData.subscriptionCurrentPeriodEnd);

  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('month');
  const onIndividualEstimatorPlan =
    subscription === 'individual-estimator-monthly' || subscription === 'individual-estimator-yearly';
  const onIndividualDesignerPlan =
    subscription !== null && ['individual-designer-monthly', 'individual-designer-yearly'].includes(subscription);

  useEffect(() => {
    if (subscription === null) return;

    // The subscription may not be loaded right away or may change, so toggle the tab if appropriate
    if (monthlyPlans.includes(subscription)) setBillingPeriod('month');
    else if (yearlyPlans.includes(subscription)) setBillingPeriod('year');
  }, [subscription]);

  if (loggingIn) return null;
  if (!loggedInUser || !isSubscriptionManager)
    return (
      <Alert severity="info">
        You must be logged in as the subscription manager to make changes to the subscription. Contact
        help@paragontruss.com for further assistance.
      </Alert>
    );

  const downgradeToFreePlan =
    subscription !== null && stripePriceLookupKeys.includes(subscription)
      ? async () => {
          const success = await dispatch(cancelSubscription());
          if (success) dispatch(setSubscription({ subscription, cancelAtPeriodEnd: true, currentPeriodEnd }));
        }
      : undefined;
  const cancelDowngrade =
    subscription !== null && stripePriceLookupKeys.includes(subscription)
      ? async () => {
          const success = await dispatch(uncancelSubscription());
          if (success) dispatch(setSubscription({ subscription, cancelAtPeriodEnd: false, currentPeriodEnd }));
        }
      : undefined;

  const startFreeTrial =
    subscription === 'free' && !loggedInUser.trialActivated
      ? async () => {
          await dispatch(startFreeTrialAction(successUrl));
        }
      : undefined;

  return (
    <Stack spacing={2} sx={{ alignItems: 'center' }}>
      {!isSubscriptionManager ? (
        <Alert severity="info">
          You must be logged in as the subscription manager to make changes to the subscription. Contact
          help@paragontruss.com for further assistance.
        </Alert>
      ) : subscription === 'none' ? (
        <Alert severity="info">
          Your account type is non-standard. Contact sales@paragontruss.com to change your plan.
        </Alert>
      ) : message ? (
        typeof message === 'string' ? (
          <Typography>{message}</Typography>
        ) : (
          message
        )
      ) : (
        showGreeting && (
          <Typography variant="h4" align="center">
            Hello, {loggedInUser.firstName}!
          </Typography>
        )
      )}

      <ToggleButtonGroup
        exclusive
        value={billingPeriod}
        onChange={(_, value) => {
          if (value) setBillingPeriod(value);
        }}
      >
        <ToggleButton value="month">Monthly</ToggleButton>
        <ToggleButton value="year">Yearly</ToggleButton>
      </ToggleButtonGroup>

      <Grid container spacing={1} sx={{ minWidth: '700px' }}>
        <Grid size={4}>
          <PlanCard
            plan={FREE_PLAN}
            billingPeriod={billingPeriod}
            displayBillingPeriod={false}
            active={subscription === 'free'}
            canUpgradeToThisPlan={false}
            canManagePaymentForThisPlan={false}
          />
        </Grid>

        <Grid size={4}>
          <PlanCard
            plan={INDIVIDUAL_ESTIMATOR_PLAN}
            billingPeriod={billingPeriod}
            currentPeriodEnd={onIndividualEstimatorPlan && currentPeriodEnd ? new Date(currentPeriodEnd) : undefined}
            active={onIndividualEstimatorPlan}
            downgradePending={cancelAtPeriodEnd}
            canUpgradeToThisPlan={subscription === 'free'}
            canManagePaymentForThisPlan={true}
            upgradeToThisPlan={() =>
              dispatch(
                upgradeToPlan(
                  billingPeriod === 'month' ? 'individual-estimator-monthly' : 'individual-estimator-yearly',
                  successUrl,
                  cancelUrl,
                ),
              )
            }
            downgradeToFreePlan={downgradeToFreePlan}
            cancelDowngrade={cancelDowngrade}
          />
        </Grid>

        <Grid size={4}>
          <PlanCard
            plan={INDIVIDUAL_DESIGNER_PLAN}
            billingPeriod={billingPeriod}
            currentPeriodEnd={onIndividualDesignerPlan && currentPeriodEnd ? new Date(currentPeriodEnd) : undefined}
            active={onIndividualDesignerPlan}
            downgradePending={cancelAtPeriodEnd}
            canUpgradeToThisPlan={subscription === 'free' || subscription === 'trial'}
            canManagePaymentForThisPlan={true}
            upgradeToThisPlan={() =>
              dispatch(
                upgradeToPlan(
                  billingPeriod === 'month' ? 'individual-designer-monthly' : 'individual-designer-yearly',
                  successUrl,
                  cancelUrl,
                ),
              )
            }
            downgradeToFreePlan={downgradeToFreePlan}
            cancelDowngrade={cancelDowngrade}
            startFreeTrial={startFreeTrial}
            freeTrialEndDate={loggedInUser.trialEndDate ?? undefined}
          />
        </Grid>
      </Grid>
    </Stack>
  );
}
